import "./AboutMe.css";
import mypic from "../../assets/mypic.webp";
import LazyVideo from "../LazyVideo/LazyVideo";
import { useRef, useEffect, useState } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import ScrollTrigger from "gsap/ScrollTrigger";
import { SOCIALS, ABOUT_CATEGORY } from "../../config";
import { fetchWorkMedia } from "../../utils/workMedia";
import { IconQuote } from "@tabler/icons-react";

gsap.registerPlugin(useGSAP, ScrollTrigger);

function AboutMe() {
  const container = useRef();
  const q = gsap.utils.selector(container);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      mm.add(
        {
          isDesktop: "(min-width: 900px)",
          isMobile: "(max-width: 899px)",
        },
        (context) => {
          let { isMobile } = context.conditions;

          // 1. Initial State
          gsap.set(
            q(
              ".about-title, .about-quote-icon, .about-profile, .about-desc",
            ),
            {
              y: isMobile ? 30 : 50, // Less distance on mobile
              opacity: 0,
              filter: "blur(10px)",
            },
          );

          // 2. Blur Fade In Animation
          gsap.to(
            q(
              ".about-title, .about-quote-icon, .about-profile, .about-desc",
            ),
            {
              scrollTrigger: {
                trigger: container.current,
                start: isMobile ? "top 75%" : "top 60%", // Earlier trigger on mobile
                end: "bottom bottom",
              },
              y: 0,
              opacity: 1,
              filter: "blur(0px)",
              stagger: isMobile ? 0.05 : 0.1, // Faster stagger
              duration: 0.8,
              ease: "power2.out",
            },
          );
        },
      );

      // 3. Intensified Parallax using Translation
      mm.add("(min-width: 1200px)", () => {
        gsap.fromTo(
          q(".about-statement"),
          {
            y: -200,
          },
          {
            scrollTrigger: {
              trigger: container.current,
              start: "top bottom",
              end: "bottom top",
              scrub: 1,
            },
            y: 200,
            ease: "none",
          },
        );
      });
    },
    { scope: container },
  );

  return (
    <div className="about" ref={container}>
      <div
        className="about-columns"
        onClick={() => window.open(SOCIALS.linkedin, "_blank")}
        data-cursor="custom"
        data-cursor-text="Check my LinkedIn"
        data-cursor-icon="arrow-up-right"
      >
        <div className="about-statement">
          <div className="about-quote-icon">
            <IconQuote size={24} stroke={1.5} aria-hidden="true" />
          </div>
          <p className="h1 about-title">
            I help founders turn a rough idea into a product real users can try in weeks.
          </p>
        </div>
        <div className="about-bio">
          {/* Profile Header */}
          <div className="about-profile">
            <img className="about-avatar" src={mypic} alt="Mohamed Ayoub" loading="lazy" />
            <div className="about-profile-info">
              <h2 className="h2">Mohamed Ayoub</h2>
              <p className="text about-role">
                Design Engineer for Human-Centered AI Products · ex Forward
                Deployed Engineer @ BMW Group
              </p>
            </div>
          </div>

          {/* Description */}
          <div className="lead about-desc">
            <p>
              I take products, websites and brands from first sketch to shipped
              code. I design the interface or the identity, then build it, so
              nothing gets lost between the mockup and what goes live.
            </p>
            <p>
              At BMW Group's Debrecen plant I spent 15 months building
              defect-tracking and quality tools with the engineers who use them,
              and presented the team's work at the RWTH Tech Impact Festival in
              Aachen. Before that I designed products at Synapse AI, built React
              interfaces at Hiedberg, and took on landing pages, brand
              identities and product work for clients on Upwork.
            </p>
            <p>
              I'm now in the M1 Interaction, Graphics &amp; Design programme at
              IP Paris, and I take a few freelance projects a term.
            </p>
          </div>
        </div>
      </div>
      <AboutPhotos />
    </div>
  );
}

// Photos of me, uploaded from /admin under the "About me" category.
// Masonry via CSS columns so any aspect ratio fits. "Large" is a Work
// setting; here every photo takes one column.
function AboutPhotos() {
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    fetchWorkMedia().then((all) => setPhotos(all.filter((m) => m.category === ABOUT_CATEGORY)));
  }, []);

  if (!photos.length) return null;
  return (
    <div className="about-photos">
      {photos.map((m) =>
        m.video ? (
          <LazyVideo key={m.id} className="about-photo" src={m.src} aria-label={m.alt || undefined} />
        ) : (
          <img key={m.id} className="about-photo" src={m.src} alt={m.alt} loading="lazy" />
        )
      )}
    </div>
  );
}

export default AboutMe;
